import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@mui/material/styles';
import { GitFork } from 'lucide-react';
import useMainAssistantSocket from '../../hooks/useMainAssistantSocket';
import useVoiceChat from '../../hooks/useVoiceChat';
import stripMarkdown from '../../utils/stripMarkdown';
import { canAgentSpeakAudio } from '../../utils/audioInputSupport';
import { agentApi } from '../../services/api';
import AttachmentMessage from './AttachmentMessage';
import MarkdownRenderer from './MarkdownRenderer';
import SubAgentEventsTree from './SubAgentEventsTree';
import { bidiTextStyle, getTextDirection } from './textDirection';
import { AI_ACTION_SURFACES } from '../../../access/surfaces';
import { useActionAccess } from '../../../access/actionAccess';
import {
  listMainAssistantSessions,
  listAgentSessions,
  archiveMainAssistantSession,
  unarchiveMainAssistantSession,
  deleteMainAssistantSession,
  forkAgentSession,
  listUserAgents,
} from './api';
import GeminiIcon from '../../assets/Google-gemini-icon.svg';
import ConversationDetailsButton from './ConversationDetailsButton';

const isCancelledToolResult = (result) => !!(
  result && (result.cancelled === true || result.data?.cancelled === true)
);

const getPersistedMessageId = (message) => {
  const rawId = message?.dbMessageId ?? message?.messageId ?? message?.message_id ?? message?.id;
  const numericId = Number(rawId);
  return Number.isInteger(numericId) && numericId > 0 ? numericId : null;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

/**
 * AgentChat - Main Assistant chat component using WebSocket
 * 
 * Features:
 * - Full-screen mode
 * - Draggable window (by header)
 * - Resizable from edges and corners
 * - Enhanced session management with search and grouping
 * - Can be opened from external controls (sidebar)
 */
const AgentChat = ({ isExternalOpen, onExternalOpenChange, hideFloatingButton = false }) => {
  const muiTheme = useTheme();
  const floatingAssistantAccess = useActionAccess(AI_ACTION_SURFACES.floatingAssistantSend);
  const theme = muiTheme.palette.mode; // 'light' or 'dark'
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // WebSocket hook
  const {
    isConnected,
    isConnecting,
    agentInfo,
    currentSessionId,
    currentSessionData,
    liveUsage,
    messages,
    hasMoreMessages,
    isLoadingMoreMessages,
    isTyping,
    isLoadingSession,
    streamingContent,
    error,
    currentExecutionId,
    connect,
    disconnect,
    switchAgent,
    sendMessage,
    newSession,
    loadSession,
    loadOlderMessages,
    stopExecution,
    confirmTool,
    pendingConfirmation,
    clearError,
    setOnSessionTitleUpdate,
    setOnSessionCreated,
    isSwitchingAgent,
  } = useMainAssistantSocket();

  // Agent selector state
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(null); // null = Main Assistant
  const [showAgentSelector, setShowAgentSelector] = useState(false);
  const [activeVoiceAgent, setActiveVoiceAgent] = useState(null);
  const activeAgentId = selectedAgentId || agentInfo?.id || null;

  useEffect(() => {
    if (!activeAgentId) {
      setActiveVoiceAgent(null);
      return undefined;
    }

    let cancelled = false;
    agentApi.get(activeAgentId)
      .then((agent) => {
        if (!cancelled) {
          setActiveVoiceAgent(agent);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setActiveVoiceAgent(null);
        }
        console.error('[AgentChat] Failed to fetch active voice agent:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [activeAgentId]);

  const selectedAgentRecord = useMemo(() => {
    if (selectedAgentId == null) return null;
    return agents.find((agent) => String(agent.id) === String(selectedAgentId)) || null;
  }, [agents, selectedAgentId]);

  const mainAssistantAgent = useMemo(
    () => {
      const mainAssistants = agents.filter((agent) => agent.agent_type === 'MAIN_ASSISTANT');
      return mainAssistants.sort((a, b) => {
        const defaultDelta = Number(Boolean(b.is_default)) - Number(Boolean(a.is_default));
        if (defaultDelta !== 0) return defaultDelta;
        const aCreated = Date.parse(a.created_at || a.createdAt || '');
        const bCreated = Date.parse(b.created_at || b.createdAt || '');
        return (Number.isNaN(bCreated) ? 0 : bCreated) - (Number.isNaN(aCreated) ? 0 : aCreated);
      })[0] || null;
    },
    [agents]
  );

  const defaultMainAssistantName = selectedAgentId == null
    ? (agentInfo?.name || mainAssistantAgent?.name || 'Main Assistant')
    : (mainAssistantAgent?.name || 'Main Assistant');
  const defaultMainAssistantId = selectedAgentId == null
    ? (agentInfo?.id || mainAssistantAgent?.id || null)
    : (mainAssistantAgent?.id || null);
  const defaultMainAssistantModelName = selectedAgentId == null
    ? (
        agentInfo?.model_name
        || agentInfo?.model?.name
        || agentInfo?.model?.model
        || mainAssistantAgent?.model_name
        || mainAssistantAgent?.model?.name
        || mainAssistantAgent?.model?.model
        || 'Not set'
      )
    : (
        mainAssistantAgent?.model_name
        || mainAssistantAgent?.model?.name
        || mainAssistantAgent?.model?.model
        || 'Not set'
      );

  const conversationAgentName = selectedAgentId != null
    ? (selectedAgentRecord?.name || 'Agent')
    : defaultMainAssistantName;
  const conversationModelName = selectedAgentId != null
    ? (
        selectedAgentRecord?.model_name
        || selectedAgentRecord?.model?.name
        || selectedAgentRecord?.model?.model
        || 'Not set'
      )
    : defaultMainAssistantModelName;
  const voiceAgent = activeVoiceAgent || selectedAgentRecord || (selectedAgentId == null ? (agentInfo || mainAssistantAgent) : null);
  const pendingVoiceResponseRef = useRef(false);
  const voiceResponseBaselineRef = useRef(null);
  const spokenVoiceExecutionIdsRef = useRef(new Set());
  
  const handleVoiceRecording = useCallback(async (file) => {
    if (!isConnected || isTyping) return;
    if (!floatingAssistantAccess.ensureAccess()) return;
    try {
      const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant' && m.content);
      pendingVoiceResponseRef.current = true;
      voiceResponseBaselineRef.current = lastAssistant?.id || null;
      const attachment = await agentApi.uploadAttachment(file, 'Voice recording', 'AUDIO');
      const messageContent = `**Attached Voice Recording:**\n- ${attachment.original_name} (${formatFileSize(attachment.size)}, ${attachment.attachment_type})${attachment.file_url ? ` [View](${attachment.file_url})` : ''}`;
      sendMessage(messageContent, null, { attachment_ids: [attachment.id] });
    } catch (err) {
      console.error('Voice recording upload failed:', err);
      pendingVoiceResponseRef.current = false;
      throw err;
    }
  }, [floatingAssistantAccess, isConnected, isTyping, messages, sendMessage]);

  // Voice chat — record and send audio to the LLM
  const {
    voiceState, isVoiceOn, interimTranscript,
    toggleVoice, speakResponse, stopVoice,
    ttsVoice, setTtsVoice,
    voiceRate, setVoiceRate,
    isRecordingSupported, isTTSSupported,
  } = useVoiceChat({
    onVoiceRecording: handleVoiceRecording,
    onSpeakResponse: canAgentSpeakAudio(voiceAgent)
      ? async (text, options) => {
          if (!options.executionId) {
            throw new Error('Voice output requires a completed execution.');
          }
          return agentApi.synthesizeSpeech(options.executionId, {
            text,
            voice: options.voice,
            speed: options.speed,
          });
        }
      : undefined,
  });

  // Speak AI response when isTyping transitions false → got final message
  useEffect(() => {
    if (!isVoiceOn || !pendingVoiceResponseRef.current) return;

    const lastMsg = messages.length > 0
      ? [...messages].reverse().find((m) => m.role === 'assistant' && m.content)
      : null;
    const baselineId = voiceResponseBaselineRef.current;
    const isNewVoiceResponse = Boolean(lastMsg && lastMsg.id !== baselineId);

    if (lastMsg && isNewVoiceResponse && lastMsg.completed && lastMsg.executionId) {
      if (!spokenVoiceExecutionIdsRef.current.has(lastMsg.executionId)) {
        spokenVoiceExecutionIdsRef.current.add(lastMsg.executionId);
        pendingVoiceResponseRef.current = false;
        speakResponse(stripMarkdown(lastMsg.content), lastMsg.executionId);
      }
      return;
    }

    if (!isTyping && voiceState === 'processing' && isNewVoiceResponse && !lastMsg?.executionId) {
      pendingVoiceResponseRef.current = false;
      stopVoice();
    }
  }, [isTyping, isVoiceOn, messages, speakResponse, stopVoice, voiceState]);

  // UI State
  const [isOpenInternal, setIsOpenInternal] = useState(false);
  
  // Sync with external control if provided
  useEffect(() => {
    if (isExternalOpen !== undefined) {
      setIsOpenInternal(isExternalOpen);
    }
  }, [isExternalOpen]);
  
  const setIsOpen = useCallback((value) => {
    const newValue = typeof value === 'function' ? value(isOpenInternal) : value;
    setIsOpenInternal(newValue);
    if (onExternalOpenChange) {
      onExternalOpenChange(newValue);
    }
  }, [onExternalOpenChange, isOpenInternal]);
  
  // Alias isOpen to the internal state for reading
  const isOpen = isExternalOpen !== undefined ? isExternalOpen : isOpenInternal;
  
  const [isMinimized, setIsMinimized] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(240);
  const [sessionSearch, setSessionSearch] = useState('');
  
  // Pagination state
  const [hasMoreSessions, setHasMoreSessions] = useState(false);
  const [nextPage, setNextPage] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const sessionsListRef = useRef(null);
  
  // Request tracking ref to prevent race conditions when switching agents
  const fetchSessionsRequestIdRef = useRef(0);
  
  // Window state - fullscreen, draggable, resizable
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [size, setSize] = useState({ width: 800, height: 600 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  
  // Mobile detection
  const isMobile = window.innerWidth <= 768;
  const isTablet = window.innerWidth > 768 && window.innerWidth <= 1024;
  
  // Drag/Resize refs
  const dragStartPos = useRef({ x: 0, y: 0 });
  const dragStartWindowPos = useRef({ x: 0, y: 0 });
  const resizeStartPos = useRef({ x: 0, y: 0 });
  const resizeStartSize = useRef({ width: 0, height: 0 });
  const resizeStartWindowPos = useRef({ x: 0, y: 0 });
  
  // Min/max constraints - responsive
  const minSize = { width: isMobile ? 300 : 400, height: isMobile ? 400 : 300 };
  const minSidebarWidth = isMobile ? 0 : 180;
  const maxSidebarWidth = isMobile ? window.innerWidth * 0.85 : 400;
  
  // Auto-scroll to bottom when new messages arrive
  const prevMessageCountRef = useRef(0);
  const isPrependingMessagesRef = useRef(false);
  const wasLoadingMoreMessagesRef = useRef(false);
  useEffect(() => {
    if (messages.length > prevMessageCountRef.current && !isPrependingMessagesRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevMessageCountRef.current = messages.length;
  }, [messages, streamingContent]);

  useEffect(() => {
    if (wasLoadingMoreMessagesRef.current && !isLoadingMoreMessages) {
      isPrependingMessagesRef.current = false;
    }
    wasLoadingMoreMessagesRef.current = isLoadingMoreMessages;
  }, [isLoadingMoreMessages]);

  const handleLoadOlderMessages = useCallback(() => {
    isPrependingMessagesRef.current = true;
    const started = loadOlderMessages();
    if (!started) {
      isPrependingMessagesRef.current = false;
    }
  }, [loadOlderMessages]);
  
  // Connect when chat opens
  useEffect(() => {
    // Don't auto-connect if we're in the middle of switching agents
    if (isOpen && !isConnected && !isConnecting && !isSwitchingAgent()) {
      connect();
    }
  }, [isOpen, isConnected, isConnecting, connect, isSwitchingAgent]);
  
  // Fetch sessions from API based on selected agent
  const fetchSessions = useCallback(async (reset = true) => {
    // Increment request ID to track this specific request
    const requestId = ++fetchSessionsRequestIdRef.current;
    
    if (reset) {
      setSessionsLoading(true);
      setSessions([]);
    }
    try {
      let response;
      const pageSize = 15; // Small page size for fast initial load
      if (selectedAgentId) {
        // Fetch sessions for the specific agent
        response = await listAgentSessions(selectedAgentId, {
          is_active: !showArchived,
          page_size: pageSize,
        });
      } else {
        // Fetch main assistant sessions
        response = await listMainAssistantSessions({
          archived: showArchived,
          page_size: pageSize,
        });
      }
      
      // Check if this is still the latest request (ignore stale responses)
      if (requestId !== fetchSessionsRequestIdRef.current) {
        return;
      }
      
      let sessionsList = response?.results || response?.data?.results || [];
      const nextPageUrl = response?.next || response?.data?.next || null;
      
      // Safety filter: ensure sessions match the current agent selection
      // This prevents displaying sessions from wrong agent due to race conditions
      if (selectedAgentId) {
        // For non-Main Assistant agents, filter by agent_id
        sessionsList = sessionsList.filter(s => s.agent_id === selectedAgentId || s.agent?.id === selectedAgentId);
      } else {
        // For Main Assistant, filter to sessions where agent_type is MAIN_ASSISTANT
        sessionsList = sessionsList.filter(s => {
          const agentType = s.agent_type || s.agent?.agent_type;
          return agentType === 'MAIN_ASSISTANT';
        });
      }
      
      setSessions(sessionsList);
      setHasMoreSessions(!!nextPageUrl);
      setNextPage(nextPageUrl);
    } catch (err) {
      console.error('[AgentChat] Failed to fetch sessions:', err);
    } finally {
      // Only update loading state if this is still the latest request
      if (requestId === fetchSessionsRequestIdRef.current) {
        setSessionsLoading(false);
      }
    }
  }, [showArchived, selectedAgentId]);
  
  // Load more sessions (pagination)
  const loadMoreSessions = useCallback(async () => {
    if (!hasMoreSessions || isLoadingMore || !nextPage) return;
    
    // Track the current agent to detect if agent changed during request
    const requestAgentId = selectedAgentId;
    setIsLoadingMore(true);
    try {
      // Extract page number from nextPage URL
      const url = new URL(nextPage);
      const page = url.searchParams.get('page') || 2;
      
      let response;
      const pageSize = 15;
      if (selectedAgentId) {
        response = await listAgentSessions(selectedAgentId, {
          is_active: !showArchived,
          page_size: pageSize,
          page: page,
        });
      } else {
        response = await listMainAssistantSessions({
          archived: showArchived,
          page_size: pageSize,
          page: page,
        });
      }
      
      // Check if agent changed during request - ignore stale response
      if (requestAgentId !== selectedAgentId) {
        return;
      }
      
      let newSessions = response?.results || response?.data?.results || [];
      const nextPageUrl = response?.next || response?.data?.next || null;
      
      // Safety filter: ensure sessions match the current agent selection
      if (selectedAgentId) {
        newSessions = newSessions.filter(s => s.agent_id === selectedAgentId || s.agent?.id === selectedAgentId);
      } else {
        newSessions = newSessions.filter(s => {
          const agentType = s.agent_type || s.agent?.agent_type;
          return agentType === 'MAIN_ASSISTANT';
        });
      }
      
      
      setSessions(prev => [...prev, ...newSessions]);
      setHasMoreSessions(!!nextPageUrl);
      setNextPage(nextPageUrl);
    } catch (err) {
      console.error('[AgentChat] Failed to load more sessions:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMoreSessions, isLoadingMore, nextPage, selectedAgentId, showArchived]);
  
  // Handle scroll in sessions list for infinite scroll
  const handleSessionsScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Load more when user scrolls near the bottom (within 100px)
    if (scrollHeight - scrollTop - clientHeight < 100) {
      loadMoreSessions();
    }
  }, [loadMoreSessions]);
  
  // Clear sessions immediately when agent changes (before fetch completes)
  useEffect(() => {
    setSessions([]);
    setHasMoreSessions(false);
    setNextPage(null);
  }, [selectedAgentId]);
  
  // Fetch sessions when connected or agent changes
  useEffect(() => {
    if (isConnected) {
      fetchSessions();
    }
  }, [isConnected, showArchived, selectedAgentId, fetchSessions]);
  
  // Fetch available agents when chat opens
  useEffect(() => {
    if (isOpen) {
      listUserAgents({ is_active: true })
        .then(response => {
          setAgents(response.results || response || []);
        })
        .catch(err => {
          console.error('[AgentChat] Failed to fetch agents:', err);
        });
    }
  }, [isOpen]);
  
  // Set up session title update callback
  useEffect(() => {
    setOnSessionTitleUpdate((sessionId, title) => {
      setSessions(prev => prev.map(s => 
        s.id === sessionId ? { ...s, title } : s
      ));
    });
  }, [setOnSessionTitleUpdate]);

  // Set up session created callback to refresh session list
  useEffect(() => {
    setOnSessionCreated(() => {
      // Refresh the session list when a new session is created
      fetchSessions();
    });
  }, [setOnSessionCreated, fetchSessions]);
  
  // Initialize position on open - position at bottom right with small margin
  useEffect(() => {
    if (isOpen && !isFullscreen) {
      setPosition({
        x: window.innerWidth - size.width - 10,
        y: window.innerHeight - size.height - 10,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  
  // Filter and group sessions by date
  const groupedSessions = useMemo(() => {
    const filtered = sessions.filter(s => 
      !sessionSearch || 
      (s.title || 'Untitled Chat').toLowerCase().includes(sessionSearch.toLowerCase())
    );
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    const lastMonth = new Date(today);
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    
    const groups = { today: [], yesterday: [], lastWeek: [], lastMonth: [], older: [] };
    
    filtered.forEach(session => {
      const date = new Date(session.last_message_at || session.created_at);
      if (date >= today) groups.today.push(session);
      else if (date >= yesterday) groups.yesterday.push(session);
      else if (date >= lastWeek) groups.lastWeek.push(session);
      else if (date >= lastMonth) groups.lastMonth.push(session);
      else groups.older.push(session);
    });
    
    return groups;
  }, [sessions, sessionSearch]);
  
  // Handle send message
  const handleSend = useCallback(() => {
    if (!inputValue.trim() || isTyping) return;
    if (!floatingAssistantAccess.ensureAccess()) return;
    
    sendMessage(inputValue);
    setInputValue('');
    inputRef.current?.focus();
  }, [floatingAssistantAccess, inputValue, isTyping, sendMessage]);
  
  // Handle key press in input
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);
  
  // Handle new session
  const handleNewSession = useCallback(() => {
    newSession();
    // Clear messages for new session
    // Sessions will be refreshed when session_created event is received
  }, [newSession]);
  
  // Handle session click
  const handleSessionClick = useCallback((sessionId) => {
    if (sessionId !== currentSessionId) {
      loadSession(sessionId);
    }
  }, [currentSessionId, loadSession]);
  
  // Handle archive session
  const handleArchiveSession = useCallback(async (sessionId, e) => {
    e.stopPropagation();
    try {
      if (showArchived) {
        await unarchiveMainAssistantSession(sessionId);
      } else {
        await archiveMainAssistantSession(sessionId);
      }
      fetchSessions();
    } catch (err) {
      console.error('[AgentChat] Failed to archive session:', err);
    }
  }, [showArchived, fetchSessions]);

  const handleForkSession = useCallback(async (sessionId, e, messageId = null) => {
    e?.stopPropagation();
    try {
      const response = await forkAgentSession(
        sessionId,
        messageId ? { message_id: messageId } : {}
      );
      const forkedSessionId = response?.data?.id || response?.id;
      await fetchSessions();
      if (forkedSessionId) {
        loadSession(forkedSessionId);
      }
    } catch (err) {
      console.error('[AgentChat] Failed to fork session:', err);
    }
  }, [fetchSessions, loadSession]);
  
  // Handle delete session
  const handleDeleteSession = useCallback(async (sessionId, e) => {
    e.stopPropagation();
    if (!confirm('Delete this chat permanently?')) return;
    try {
      await deleteMainAssistantSession(sessionId);
      fetchSessions();
    } catch (err) {
      console.error('[AgentChat] Failed to delete session:', err);
    }
  }, [fetchSessions]);
  
  // Drag handlers
  const handleDragStart = useCallback((e) => {
    if (isFullscreen || e.target.tagName === 'BUTTON') return;
    e.preventDefault();
    setIsDragging(true);
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    dragStartWindowPos.current = { ...position };
  }, [isFullscreen, position]);
  
  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    const deltaX = e.clientX - dragStartPos.current.x;
    const deltaY = e.clientY - dragStartPos.current.y;
    // Allow window to go right up to the edge (just keep 1px visible on each side)
    const newX = Math.max(-size.width + 100, Math.min(window.innerWidth - 100, dragStartWindowPos.current.x + deltaX));
    const newY = Math.max(0, Math.min(window.innerHeight - 50, dragStartWindowPos.current.y + deltaY));
    setPosition({ x: newX, y: newY });
  }, [isDragging, size]);
  
  const handleDragEnd = useCallback(() => setIsDragging(false), []);
  
  // Resize handlers
  const handleResizeStart = useCallback((direction) => (e) => {
    if (isFullscreen) return;
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeDirection(direction);
    resizeStartPos.current = { x: e.clientX, y: e.clientY };
    resizeStartSize.current = { ...size };
    resizeStartWindowPos.current = { ...position };
  }, [isFullscreen, size, position]);
  
  const handleResizeMove = useCallback((e) => {
    if (!isResizing || !resizeDirection) return;
    const deltaX = e.clientX - resizeStartPos.current.x;
    const deltaY = e.clientY - resizeStartPos.current.y;
    let newWidth = resizeStartSize.current.width;
    let newHeight = resizeStartSize.current.height;
    let newX = resizeStartWindowPos.current.x;
    let newY = resizeStartWindowPos.current.y;
    
    if (resizeDirection.includes('e')) newWidth = Math.max(minSize.width, resizeStartSize.current.width + deltaX);
    if (resizeDirection.includes('w')) {
      const widthDelta = Math.min(deltaX, resizeStartSize.current.width - minSize.width);
      newWidth = resizeStartSize.current.width - widthDelta;
      newX = resizeStartWindowPos.current.x + widthDelta;
    }
    if (resizeDirection.includes('s')) newHeight = Math.max(minSize.height, resizeStartSize.current.height + deltaY);
    if (resizeDirection.includes('n')) {
      const heightDelta = Math.min(deltaY, resizeStartSize.current.height - minSize.height);
      newHeight = resizeStartSize.current.height - heightDelta;
      newY = resizeStartWindowPos.current.y + heightDelta;
    }
    setSize({ width: newWidth, height: newHeight });
    setPosition({ x: newX, y: newY });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResizing, resizeDirection]);
  
  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    setResizeDirection(null);
  }, []);
  
  // Sidebar resize handlers
  const handleSidebarResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizingSidebar(true);
    resizeStartPos.current = { x: e.clientX, y: 0 };
  }, []);
  
  const handleSidebarResizeMove = useCallback((e) => {
    if (!isResizingSidebar) return;
    const delta = e.clientX - resizeStartPos.current.x;
    setSidebarWidth(prev => Math.max(minSidebarWidth, Math.min(maxSidebarWidth, prev + delta)));
    resizeStartPos.current = { x: e.clientX, y: 0 };
  }, [isResizingSidebar]);
  
  const handleSidebarResizeEnd = useCallback(() => setIsResizingSidebar(false), []);
  
  // Global mouse event handlers for drag/resize
  useEffect(() => {
    const handleMouseMove = (e) => {
      handleDragMove(e);
      handleResizeMove(e);
      handleSidebarResizeMove(e);
    };
    const handleMouseUp = () => {
      handleDragEnd();
      handleResizeEnd();
      handleSidebarResizeEnd();
    };
    if (isDragging || isResizing || isResizingSidebar) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, isResizingSidebar, handleDragMove, handleResizeMove, handleSidebarResizeMove, handleDragEnd, handleResizeEnd, handleSidebarResizeEnd]);
  
  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => setIsFullscreen(prev => !prev), []);
  
  // Theme colors
  const colors = {
    bg: theme === 'dark' ? '#1f2937' : '#ffffff',
    bgSecondary: theme === 'dark' ? '#111827' : '#f9fafb',
    text: theme === 'dark' ? '#e5e7eb' : '#1f2937',
    textMuted: theme === 'dark' ? '#9ca3af' : '#6b7280',
    border: theme === 'dark' ? '#374151' : '#e5e7eb',
    primary: '#3b82f6',
    userBubble: theme === 'dark' ? '#374151' : '#e5e7eb',
    assistantBubble: theme === 'dark' ? '#1f2937' : '#ffffff',
  };
  
  // Filtered sessions based on search
  const filteredGroupedSessions = useMemo(() => {
    if (!sessionSearch.trim()) return groupedSessions;
    const search = sessionSearch.toLowerCase();
    const filterGroup = (sessions) => sessions.filter(s => 
      (s.title || '').toLowerCase().includes(search)
    );
    return {
      today: filterGroup(groupedSessions.today),
      yesterday: filterGroup(groupedSessions.yesterday),
      lastWeek: filterGroup(groupedSessions.lastWeek),
      lastMonth: filterGroup(groupedSessions.lastMonth),
      older: filterGroup(groupedSessions.older),
    };
  }, [groupedSessions, sessionSearch]);
  
  // Render session group
  const renderSessionGroup = (title, sessions) => {
    if (!sessions || sessions.length === 0) return null;
    return (
      <div key={title} style={{ marginBottom: '12px' }}>
        <div style={{ 
          fontSize: '11px', 
          fontWeight: 600, 
          color: colors.textMuted, 
          padding: '4px 8px', 
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          {title}
        </div>
        {sessions.map(session => renderSessionItem(session))}
      </div>
    );
  };
  
  // Render session item
  const renderSessionItem = (session) => {
    const sessionTitle = session.title || session.first_message_preview || 'Untitled Chat';

    return (
    <div
      key={session.id}
      onClick={() => handleSessionClick(session.id)}
      style={{
        padding: '10px 12px',
        borderRadius: '8px',
        cursor: 'pointer',
        marginBottom: '4px',
        backgroundColor: session.id === currentSessionId ? colors.primary + '20' : 'transparent',
        border: session.id === currentSessionId ? `1px solid ${colors.primary}` : '1px solid transparent',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        transition: 'background-color 0.15s',
      }}
      onMouseEnter={(e) => { if (session.id !== currentSessionId) e.currentTarget.style.backgroundColor = colors.border + '40'; }}
      onMouseLeave={(e) => { if (session.id !== currentSessionId) e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <div style={{
          ...bidiTextStyle,
          fontSize: '13px',
          color: colors.text,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontWeight: session.id === currentSessionId ? 500 : 400,
        }} dir={getTextDirection(sessionTitle)}>
          {sessionTitle}
        </div>
        <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '2px' }}>
          {new Date(session.last_message_at || session.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '4px', opacity: 0.6 }} className="session-actions">
        <button
          onClick={(e) => handleArchiveSession(session.id, e)}
          style={{ padding: '4px 6px', border: 'none', background: 'transparent', color: colors.textMuted, cursor: 'pointer', fontSize: '12px', borderRadius: '4px' }}
          title={showArchived ? 'Restore' : 'Archive'}
        >
          {showArchived ? '↩' : '📥'}
        </button>
        <button
          onClick={(e) => handleDeleteSession(session.id, e)}
          style={{ padding: '4px 6px', border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer', fontSize: '12px', borderRadius: '4px' }}
          title="Delete"
        >
          🗑
        </button>
      </div>
    </div>
    );
  };
  
  // Resize handle style helper
  const resizeHandleStyle = (cursor, position) => ({
    position: 'absolute',
    ...position,
    cursor: isFullscreen ? 'default' : cursor,
    zIndex: 10,
  });
  
  if (!isOpen) {
    // If hideFloatingButton is true, don't render anything when closed
    if (hideFloatingButton) {
      return null;
    }
    return (
      <button
        className="chat-toggle-button"
        onClick={() => setIsOpen(true)}
        title="Open Chat"
        style={{
          position: 'fixed',
          bottom: isMobile ? '20px' : '50px',
          right: isMobile ? '16px' : '30px',
          width: isMobile ? '52px' : '60px',
          height: isMobile ? '52px' : '60px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${colors.primary}, #8b5cf6)`,
          border: 'none',
          boxShadow: '0 4px 20px rgba(59, 130, 246, 0.4)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s ease',
          zIndex: 9998,
          // Safe area for mobile
          marginBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
      >
        <img src={GeminiIcon} alt="Chat" style={{ width: isMobile ? '24px' : '28px', height: isMobile ? '24px' : '28px', filter: 'brightness(10)' }} />
      </button>
    );
  }
  
  // Minimized state - show a small bar at the bottom
  if (isMinimized) {
    return createPortal(
      <div
        style={{
          position: 'fixed',
          bottom: isMobile ? '12px' : '20px',
          right: isMobile ? '12px' : '20px',
          width: isMobile ? 'calc(100% - 24px)' : '280px',
          maxWidth: '320px',
          backgroundColor: colors.bg,
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
          border: `1px solid ${colors.border}`,
          zIndex: 9999,
          overflow: 'hidden',
          // Safe area
          marginBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 14px',
            backgroundColor: colors.bgSecondary,
            cursor: 'pointer',
          }}
          onClick={() => setIsMinimized(false)}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ 
              width: '32px', 
              height: '32px', 
              borderRadius: '8px', 
              background: `linear-gradient(135deg, ${colors.primary}, #8b5cf6)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <img src={GeminiIcon} alt="Assistant" style={{ width: '18px', height: '18px', filter: 'brightness(10)' }} />
            </div>
            <div>
              <div style={{ fontWeight: 600, color: colors.text, fontSize: '14px' }}>
                {conversationAgentName}
              </div>
              <div style={{ fontSize: '11px', color: isConnected ? '#22c55e' : colors.textMuted }}>
                {isConnected ? 'Online' : 'Offline'}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={(e) => { e.stopPropagation(); setIsMinimized(false); }}
              style={{
                padding: '6px 8px',
                borderRadius: '6px',
                border: 'none',
                background: 'transparent',
                color: colors.text,
                cursor: 'pointer',
                fontSize: '14px',
              }}
              title="Expand"
            >
              ▲
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); disconnect(); setIsOpen(false); setIsMinimized(false); }}
              style={{
                padding: '6px 8px',
                borderRadius: '6px',
                border: 'none',
                background: 'transparent',
                color: colors.textMuted,
                cursor: 'pointer',
                fontSize: '16px',
              }}
              title="Close"
            >
              ×
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }
  
  return createPortal(
    <div
      style={{
        position: 'fixed',
        // On mobile, always fullscreen. On tablet/desktop, use normal sizing
        ...((isFullscreen || isMobile) ? {
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
          borderRadius: 0,
        } : {
          top: `${position.y}px`,
          left: `${position.x}px`,
          width: `${size.width}px`,
          height: `${size.height}px`,
          borderRadius: '12px',
        }),
        backgroundColor: colors.bg,
        boxShadow: (isFullscreen || isMobile) ? 'none' : '0 8px 32px rgba(0, 0, 0, 0.25)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 9999,
        border: (isFullscreen || isMobile) ? 'none' : `1px solid ${colors.border}`,
        transition: isDragging || isResizing ? 'none' : 'border-radius 0.2s',
        // Safe area padding for mobile notches
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : 0,
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : 0,
      }}
      onClick={() => setShowAgentSelector(false)}
    >
      {/* Resize handles (only when not fullscreen and not mobile) */}
      {!isFullscreen && !isMobile && (
        <>
          {/* Corners */}
          <div onMouseDown={handleResizeStart('nw')} style={resizeHandleStyle('nw-resize', { top: 0, left: 0, width: '12px', height: '12px' })} />
          <div onMouseDown={handleResizeStart('ne')} style={resizeHandleStyle('ne-resize', { top: 0, right: 0, width: '12px', height: '12px' })} />
          <div onMouseDown={handleResizeStart('sw')} style={resizeHandleStyle('sw-resize', { bottom: 0, left: 0, width: '12px', height: '12px' })} />
          <div onMouseDown={handleResizeStart('se')} style={resizeHandleStyle('se-resize', { bottom: 0, right: 0, width: '12px', height: '12px' })} />
          {/* Edges */}
          <div onMouseDown={handleResizeStart('n')} style={resizeHandleStyle('n-resize', { top: 0, left: '12px', right: '12px', height: '6px' })} />
          <div onMouseDown={handleResizeStart('s')} style={resizeHandleStyle('s-resize', { bottom: 0, left: '12px', right: '12px', height: '6px' })} />
          <div onMouseDown={handleResizeStart('w')} style={resizeHandleStyle('w-resize', { left: 0, top: '12px', bottom: '12px', width: '6px' })} />
          <div onMouseDown={handleResizeStart('e')} style={resizeHandleStyle('e-resize', { right: 0, top: '12px', bottom: '12px', width: '6px' })} />
        </>
      )}
      
      {/* Header - Draggable */}
      <div
        onMouseDown={!isMobile ? handleDragStart : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: isMobile ? '10px 12px' : '12px 16px',
          borderBottom: `1px solid ${colors.border}`,
          backgroundColor: colors.bgSecondary,
          cursor: (isFullscreen || isMobile) ? 'default' : 'move',
          userSelect: 'none',
          flexWrap: 'nowrap',
          gap: '8px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', flex: 1, minWidth: 0 }}>
          <div style={{ 
            width: isMobile ? '32px' : '36px', 
            height: isMobile ? '32px' : '36px', 
            borderRadius: '10px', 
            background: `linear-gradient(135deg, ${colors.primary}, #8b5cf6)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <img src={GeminiIcon} alt="Assistant" style={{ width: isMobile ? '16px' : '20px', height: isMobile ? '16px' : '20px', filter: 'brightness(10)' }} />
          </div>
          <div style={{ position: 'relative', minWidth: 0, flex: 1 }}>
            <div 
              onClick={(e) => { e.stopPropagation(); setShowAgentSelector(!showAgentSelector); }}
              style={{ 
                fontWeight: 600, 
                color: colors.text, 
                fontSize: isMobile ? '13px' : '15px', 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {conversationAgentName}
              <ConversationDetailsButton
                sessionId={currentSessionId}
                sessionData={currentSessionData}
                messages={messages}
                agentName={conversationAgentName}
                modelName={conversationModelName}
                statusLabel={isTyping ? 'Running' : (currentSessionId ? 'Idle' : 'No active session')}
                currentExecutionId={currentExecutionId || liveUsage?.executionId}
                liveSessionId={liveUsage?.sessionId || currentSessionId}
                liveTotalTokens={liveUsage?.totalTokens}
                liveTotalCost={liveUsage?.totalCost}
                isExecuting={isTyping}
                buttonSx={{ color: colors.text, p: 0.25, ml: 0.25 }}
                iconSx={{ fontSize: isMobile ? 17 : 19 }}
              />
              <span style={{ fontSize: '10px', opacity: 0.6 }}>▼</span>
            </div>
            <div style={{ fontSize: '12px', color: isConnected ? '#22c55e' : colors.textMuted, display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ 
                width: '6px', 
                height: '6px', 
                borderRadius: '50%', 
                backgroundColor: isConnecting ? '#f59e0b' : isConnected ? '#22c55e' : '#ef4444',
              }} />
              {isConnecting ? 'Connecting...' : isConnected ? 'Online' : 'Offline'}
            </div>
            
            {/* Agent Selector Dropdown */}
            {showAgentSelector && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '8px',
                  minWidth: '200px',
                  backgroundColor: colors.bg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 100,
                  overflow: 'hidden',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ padding: '8px', borderBottom: `1px solid ${colors.border}`, fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase' }}>
                  Select Agent
                </div>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {/* Main Assistant (default) */}
                  <div
                    onClick={() => {
                      if (selectedAgentId !== null) {
                        setSelectedAgentId(null);
                        setShowAgentSelector(false);
                        switchAgent(null);
                      } else {
                        setShowAgentSelector(false);
                      }
                    }}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      backgroundColor: selectedAgentId === null ? colors.primary + '20' : 'transparent',
                      borderLeft: selectedAgentId === null ? `3px solid ${colors.primary}` : '3px solid transparent',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <span style={{ fontSize: '14px' }}>🤖</span>
                    <div>
                      <div style={{ fontSize: '13px', color: colors.text, fontWeight: selectedAgentId === null ? 500 : 400 }}>{defaultMainAssistantName}</div>
                      <div style={{ fontSize: '11px', color: colors.textMuted }}>
                        Default main assistant{defaultMainAssistantId ? ` - ID ${defaultMainAssistantId}` : ''}
                      </div>
                    </div>
                  </div>
                  
                  {/* Other agents */}
                  {agents.filter(a => a.agent_type !== 'MAIN_ASSISTANT').map(agent => (
                    <div
                      key={agent.id}
                      onClick={() => {
                        if (selectedAgentId !== agent.id) {
                          setSelectedAgentId(agent.id);
                          setShowAgentSelector(false);
                          switchAgent(agent.id);
                        } else {
                          setShowAgentSelector(false);
                        }
                      }}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        backgroundColor: selectedAgentId === agent.id ? colors.primary + '20' : 'transparent',
                        borderLeft: selectedAgentId === agent.id ? `3px solid ${colors.primary}` : '3px solid transparent',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <span style={{ fontSize: '14px' }}>{agent.agent_type === 'AI_SYSTEM_BUILDER' ? '🔧' : '💬'}</span>
                      <div>
                        <div style={{ fontSize: '13px', color: colors.text, fontWeight: selectedAgentId === agent.id ? 500 : 400 }}>{agent.name}</div>
                        <div style={{ fontSize: '11px', color: colors.textMuted }}>{agent.agent_type}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: isMobile ? '4px' : '6px', flexShrink: 0 }}>
          <button
            onClick={handleNewSession}
            style={{
              padding: isMobile ? '6px 10px' : '8px 14px',
              borderRadius: '8px',
              border: 'none',
              background: `linear-gradient(135deg, ${colors.primary}, #8b5cf6)`,
              color: '#fff',
              cursor: 'pointer',
              fontSize: isMobile ? '12px' : '13px',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <span style={{ fontSize: isMobile ? '12px' : '14px' }}>+</span> {isMobile ? '' : 'New Chat'}
          </button>
          <button
            onClick={() => setSidebarVisible(!sidebarVisible)}
            style={{
              padding: isMobile ? '6px' : '8px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              background: sidebarVisible ? colors.border + '40' : 'transparent',
              color: colors.text,
              cursor: 'pointer',
              fontSize: isMobile ? '14px' : '16px',
            }}
            title="Toggle sidebar"
          >
            ☰
          </button>
          <button
            onClick={() => setIsMinimized(true)}
            style={{
              padding: isMobile ? '6px' : '8px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              background: 'transparent',
              color: colors.text,
              cursor: 'pointer',
              fontSize: isMobile ? '12px' : '14px',
            }}
            title="Minimize"
          >
            ─
          </button>
          {/* Hide fullscreen toggle on mobile since it's always fullscreen */}
          {!isMobile && (
            <button
              onClick={toggleFullscreen}
              style={{
                padding: '8px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                background: 'transparent',
                color: colors.text,
                cursor: 'pointer',
                fontSize: '14px',
              }}
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? '⊙' : '⛶'}
            </button>
          )}
          <button
            onClick={() => { disconnect(); setIsOpen(false); }}
            style={{
              padding: isMobile ? '6px' : '8px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              background: 'transparent',
              color: colors.textMuted,
              cursor: 'pointer',
              fontSize: isMobile ? '16px' : '18px',
              lineHeight: 1,
            }}
            title="Close"
          >
            ×
          </button>
        </div>
      </div>
      
      {/* Main content */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
        {/* Sidebar - on mobile, overlay with backdrop */}
        {sidebarVisible && (
          <>
            {/* Mobile backdrop */}
            {isMobile && (
              <div 
                onClick={() => setSidebarVisible(false)}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  zIndex: 10,
                }}
              />
            )}
            <div
              style={{
                width: isMobile ? '85%' : `${sidebarWidth}px`,
                maxWidth: isMobile ? '300px' : `${maxSidebarWidth}px`,
                minWidth: isMobile ? '240px' : `${minSidebarWidth}px`,
                borderRight: `1px solid ${colors.border}`,
                backgroundColor: colors.bgSecondary,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                position: isMobile ? 'absolute' : 'relative',
                top: isMobile ? 0 : 'auto',
                left: isMobile ? 0 : 'auto',
                bottom: isMobile ? 0 : 'auto',
                zIndex: isMobile ? 20 : 'auto',
                boxShadow: isMobile ? '4px 0 12px rgba(0,0,0,0.15)' : 'none',
              }}
            >
            {/* Search and archive toggle */}
            <div style={{ padding: '12px', borderBottom: `1px solid ${colors.border}` }}>
              <input
                type="text"
                value={sessionSearch}
                onChange={(e) => setSessionSearch(e.target.value)}
                placeholder="Search chats..."
                dir={getTextDirection(sessionSearch)}
                style={{
                  ...bidiTextStyle,
                  direction: getTextDirection(sessionSearch),
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.bg,
                  color: colors.text,
                  fontSize: '13px',
                  outline: 'none',
                  marginBottom: '8px',
                }}
              />
              <button
                onClick={() => setShowArchived(!showArchived)}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '6px',
                  border: 'none',
                  background: showArchived ? colors.primary : colors.border + '40',
                  color: showArchived ? '#fff' : colors.text,
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                {showArchived ? '📂 Showing Archived' : '📁 Show Archived'}
              </button>
            </div>
            
            {/* Sessions list - grouped with scroll pagination */}
            <div 
              ref={sessionsListRef}
              onScroll={handleSessionsScroll}
              style={{ flex: 1, overflowY: 'auto', padding: '8px' }}
            >
              {sessionsLoading ? (
                <div style={{ textAlign: 'center', color: colors.textMuted, padding: '24px' }}>
                  <div style={{ fontSize: '20px', marginBottom: '8px' }}>⏳</div>
                  Loading sessions...
                </div>
              ) : sessions.length === 0 ? (
                <div style={{ textAlign: 'center', color: colors.textMuted, padding: '24px', fontSize: '13px' }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>💬</div>
                  No {showArchived ? 'archived ' : ''}chats yet
                </div>
              ) : (
                <>
                  {renderSessionGroup('Today', filteredGroupedSessions.today)}
                  {renderSessionGroup('Yesterday', filteredGroupedSessions.yesterday)}
                  {renderSessionGroup('Last 7 Days', filteredGroupedSessions.lastWeek)}
                  {renderSessionGroup('Last 30 Days', filteredGroupedSessions.lastMonth)}
                  {renderSessionGroup('Older', filteredGroupedSessions.older)}
                  
                  {/* Loading more indicator */}
                  {isLoadingMore && (
                    <div style={{ textAlign: 'center', color: colors.textMuted, padding: '12px', fontSize: '12px' }}>
                      Loading more...
                    </div>
                  )}
                  
                  {/* Load more button (fallback for accessibility) */}
                  {hasMoreSessions && !isLoadingMore && (
                    <button
                      onClick={loadMoreSessions}
                      style={{
                        width: '100%',
                        padding: '8px',
                        marginTop: '8px',
                        borderRadius: '6px',
                        border: `1px solid ${colors.border}`,
                        background: 'transparent',
                        color: colors.textMuted,
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                    >
                      Load more sessions
                    </button>
                  )}
                </>
              )}
            </div>
            
            {/* Sidebar resize handle - hide on mobile */}
            {!isMobile && (
              <div
                onMouseDown={handleSidebarResizeStart}
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: '4px',
                  cursor: 'ew-resize',
                  backgroundColor: 'transparent',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.primary}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              />
            )}
          </div>
          </>
        )}
        
        {/* Chat area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Messages */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            {/* Error display */}
            {error && (
              <div
                style={{
                  padding: '12px',
                  backgroundColor: '#fee2e2',
                  borderRadius: '8px',
                  color: '#dc2626',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>{error}</span>
                <button
                  onClick={clearError}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}
                >
                  ×
                </button>
              </div>
            )}
            
            {/* Loading indicator when loading session */}
            {isLoadingSession && (
              <div style={{ textAlign: 'center', color: colors.textMuted, padding: '32px' }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
                <div>Loading conversation...</div>
              </div>
            )}
            
            {hasMoreMessages && !isLoadingSession && (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <button
                  onClick={handleLoadOlderMessages}
                  disabled={isLoadingMoreMessages}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: `1px solid ${colors.border}`,
                    background: isLoadingMoreMessages ? colors.bgSecondary : 'transparent',
                    color: colors.textMuted,
                    cursor: isLoadingMoreMessages ? 'default' : 'pointer',
                    fontSize: '12px',
                  }}
                >
                  {isLoadingMoreMessages ? 'Loading...' : 'Load earlier'}
                </button>
              </div>
            )}

            {/* Welcome message if no messages */}
            {messages.length === 0 && !isTyping && !isLoadingSession && (
              <div style={{ textAlign: 'center', color: colors.textMuted, padding: '32px' }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>👋</div>
                <div>Hi! How can I assist you today?</div>
              </div>
            )}
            
            {/* Messages */}
            {messages.map((msg) => {
              const isStatusMessage = msg.role === 'assistant_status';
              const forkFromMessageId = getPersistedMessageId(msg);
              const canForkFromMessage = Boolean(
                currentSessionId &&
                forkFromMessageId &&
                msg.role !== 'tool_call' &&
                msg.role !== 'validator' &&
                !isStatusMessage
              );

              return (
              <div
                key={msg.id}
                style={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  alignItems: 'flex-start',
                  gap: '6px',
                }}
              >
                {msg.role === 'tool_call' ? (
                  // Tool call display
                  <div
                    style={{
                      padding: '8px 12px',
                      backgroundColor: theme === 'dark' ? '#1e3a5f' : '#e0f2fe',
                      borderRadius: '8px',
                      fontSize: '13px',
                      color: colors.text,
                      maxWidth: isMobile ? '100%' : '80%',
                    }}
                  >
                    <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                      🔧 {msg.toolName}
                    </div>
                    {msg.toolRationale && (
                      <div
                        style={{
                          fontSize: '12px',
                          color: colors.textMuted,
                          marginBottom: '6px',
                          lineHeight: 1.45,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {msg.toolRationale}
                      </div>
                    )}
                    {msg.result !== undefined && msg.result !== null ? (
                      <div style={{ fontSize: '12px', color: isCancelledToolResult(msg.result) ? colors.textMuted : msg.success ? colors.textMuted : '#ef4444' }}>
                        {isCancelledToolResult(msg.result) ? '■ Stopped' : msg.success === true ? '✓ Completed' : '✗ Failed'}
                      </div>
                    ) : (
                      <div style={{ fontSize: '12px', color: colors.textMuted }}>
                        ⏳ Running...
                      </div>
                    )}
                    {Array.isArray(msg.subAgentEvents) && msg.subAgentEvents.length > 0 && (
                      <SubAgentEventsTree events={msg.subAgentEvents} />
                    )}
                  </div>
                ) : (
                  // Regular message
                  <>
                  {canForkFromMessage && msg.role === 'user' && (
                    <button
                      type="button"
                      onClick={(e) => handleForkSession(currentSessionId, e, forkFromMessageId)}
                      title="Fork from this message"
                      aria-label="Fork from this message"
                      style={{
                        width: '26px',
                        height: '26px',
                        marginTop: '4px',
                        borderRadius: '999px',
                        border: `1px solid ${colors.border}`,
                        background: 'transparent',
                        color: colors.textMuted,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <GitFork size={13} />
                    </button>
                  )}
                  <div
                    dir={getTextDirection(msg.content)}
                    style={{
                      ...bidiTextStyle,
                      maxWidth: isMobile ? '100%' : msg.role === 'validator' ? '90%' : '80%',
                      padding: '12px 16px',
                      borderRadius: msg.role === 'validator'
                        ? '8px'
                        : msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                      backgroundColor: msg.role === 'validator'
                        ? (msg.validation?.allowed === false
                          ? (theme === 'dark' ? '#422006' : '#fef3c7')
                          : (theme === 'dark' ? '#052e16' : '#ecfdf5'))
                        : msg.role === 'user'
                          ? colors.primary
                          : isStatusMessage
                            ? (theme === 'dark' ? 'rgba(148, 163, 184, 0.08)' : 'rgba(100, 116, 139, 0.06)')
                            : colors.assistantBubble,
                      color: msg.role === 'user' ? '#fff' : isStatusMessage ? colors.textMuted : colors.text,
                      border: msg.role === 'validator'
                        ? `1px solid ${msg.validation?.allowed === false
                          ? (theme === 'dark' ? '#92400e' : '#f59e0b')
                          : (theme === 'dark' ? '#166534' : '#86efac')}`
                        : 'none',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                      wordBreak: 'break-word',
                      fontSize: msg.role === 'validator' ? '13px' : undefined,
                    }}
                  >
                    {msg.role === 'user' ? (
                      <AttachmentMessage content={msg.content} />
                    ) : isStatusMessage ? (
                      <span>{msg.content}</span>
                    ) : (
                      // Assistant messages: render as markdown
                      <MarkdownRenderer content={msg.content} />
                    )}
                  </div>
                  {canForkFromMessage && msg.role !== 'user' && (
                    <button
                      type="button"
                      onClick={(e) => handleForkSession(currentSessionId, e, forkFromMessageId)}
                      title="Fork from this message"
                      aria-label="Fork from this message"
                      style={{
                        width: '26px',
                        height: '26px',
                        marginTop: '4px',
                        borderRadius: '999px',
                        border: `1px solid ${colors.border}`,
                        background: 'transparent',
                        color: colors.textMuted,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <GitFork size={13} />
                    </button>
                  )}
                  </>
                )}
              </div>
              );
            })}
            
            {/* Pending Confirmation Dialog */}
            {pendingConfirmation && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                }}
              >
                <div
                  style={{
                    maxWidth: isMobile ? '100%' : '80%',
                    padding: '16px',
                    borderRadius: '12px',
                    backgroundColor: theme === 'dark' ? '#422006' : '#fef3c7',
                    border: `1px solid ${theme === 'dark' ? '#92400e' : '#f59e0b'}`,
                    color: colors.text,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '18px' }}>⚠️</span>
                    <span style={{ fontWeight: 600 }}>Confirmation Required</span>
                    {pendingConfirmation.dangerLevel > 0 && (
                      <span
                        style={{
                          fontSize: '11px',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          backgroundColor: pendingConfirmation.dangerLevel >= 4 ? '#dc2626' : '#f59e0b',
                          color: '#fff',
                          fontWeight: 600,
                        }}
                      >
                        Danger Level {pendingConfirmation.dangerLevel}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                    <strong>{pendingConfirmation.toolName}</strong> wants to execute with:
                  </div>
                  {pendingConfirmation.toolArgs && Object.keys(pendingConfirmation.toolArgs).length > 0 && (
                    <pre
                      style={{
                        fontSize: '11px',
                        padding: '8px',
                        borderRadius: '6px',
                        backgroundColor: theme === 'dark' ? '#1c1917' : '#f5f5f4',
                        marginBottom: '12px',
                        overflow: 'auto',
                        maxHeight: '120px',
                      }}
                    >
                      {JSON.stringify(pendingConfirmation.toolArgs, null, 2)}
                    </pre>
                  )}
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => confirmTool(
                        pendingConfirmation.executionId,
                        pendingConfirmation.toolName,
                        false,
                        pendingConfirmation.toolCallId
                      )}
                      style={{
                        padding: '6px 16px',
                        borderRadius: '6px',
                        border: `1px solid ${colors.border}`,
                        backgroundColor: 'transparent',
                        color: colors.text,
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 500,
                      }}
                    >
                      Deny
                    </button>
                    <button
                      onClick={() => confirmTool(
                        pendingConfirmation.executionId,
                        pendingConfirmation.toolName,
                        true,
                        pendingConfirmation.toolCallId
                      )}
                      style={{
                        padding: '6px 16px',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: '#f59e0b',
                        color: '#000',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 600,
                      }}
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Streaming content */}
            {(isTyping || streamingContent) && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div
                  dir={getTextDirection(streamingContent)}
                  style={{
                    ...bidiTextStyle,
                    maxWidth: isMobile ? '100%' : '80%',
                    padding: '12px 16px',
                    borderRadius: '18px 18px 18px 4px',
                    backgroundColor: colors.assistantBubble,
                    color: colors.text,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  }}
                >
                  {streamingContent ? (
                    <MarkdownRenderer content={streamingContent} />
                  ) : (
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <span className="typing-dot">•</span>
                      <span className="typing-dot" style={{ animationDelay: '0.2s' }}>•</span>
                      <span className="typing-dot" style={{ animationDelay: '0.4s' }}>•</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          {/* Input area */}
          <div
            style={{
              padding: isMobile ? '10px 12px' : '12px 16px',
              borderTop: `1px solid ${colors.border}`,
              backgroundColor: colors.bgSecondary,
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              // Safe area for mobile keyboard
              paddingBottom: isMobile ? 'max(10px, env(safe-area-inset-bottom))' : '12px',
            }}
          >
            {/* Voice overlay */}
            {isVoiceOn && (
              <div style={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                gap: 8, paddingBottom: 4, fontSize: 13, color: voiceState === 'recording' ? '#f44336' : voiceState === 'speaking' ? '#4caf50' : '#ff9800',
              }}>
                <span>{voiceState === 'recording' ? '🎙️ Recording…' : voiceState === 'processing' ? '⏳ Processing…' : voiceState === 'speaking' ? '🔊 Speaking…' : ''}</span>
                {interimTranscript && <span style={{ color: colors.textSecondary, fontStyle: 'italic' }}>{interimTranscript}</span>}
              </div>
            )}

            <div style={{ display: 'flex', gap: isMobile ? '8px' : '12px', alignItems: 'flex-end' }}>
            <textarea
              ref={inputRef}
              dir={getTextDirection(inputValue)}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={floatingAssistantAccess.disabled ? floatingAssistantAccess.blockedMessage : isVoiceOn ? 'Recording voice message...' : 'Type your message...'}
              disabled={!isConnected || floatingAssistantAccess.disabled}
              style={{
                ...bidiTextStyle,
                direction: getTextDirection(inputValue),
                flex: 1,
                padding: isMobile ? '10px 12px' : '10px 14px',
                borderRadius: '20px',
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.bg,
                color: colors.text,
                fontSize: isMobile ? '16px' : '14px', // 16px prevents zoom on iOS
                resize: 'none',
                minHeight: isMobile ? '42px' : '44px',
                maxHeight: isMobile ? '100px' : '120px',
                outline: 'none',
                WebkitAppearance: 'none', // Remove iOS styling
              }}
              rows={1}
            />
            
            {/* Mic button — plain HTML */}
            <button
              onClick={toggleVoice}
              disabled={!isRecordingSupported || floatingAssistantAccess.disabled}
              style={{
                width: isMobile ? '42px' : '44px',
                height: isMobile ? '42px' : '44px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: !isRecordingSupported ? '#555' : voiceState === 'idle' ? colors.border : '#f44336',
                color: '#fff',
                cursor: isRecordingSupported && !floatingAssistantAccess.disabled ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isMobile ? '18px' : '20px',
                flexShrink: 0,
                opacity: isRecordingSupported && !floatingAssistantAccess.disabled ? 1 : 0.5,
                animation: voiceState === 'recording' ? 'voicePulse 1.5s infinite' : 'none',
              }}
              title={floatingAssistantAccess.disabled ? floatingAssistantAccess.blockedMessage : !isRecordingSupported ? 'Voice recording not supported in this browser' : voiceState === 'idle' ? 'Start voice recording' : voiceState === 'recording' ? 'Recording… click to send' : voiceState === 'processing' ? 'Processing…' : 'Speaking… click to stop'}
            >
              {voiceState === 'recording' ? '🎙️' : voiceState === 'processing' ? '⏳' : voiceState === 'speaking' ? '🔊' : '🎤'}
            </button>

            {isTyping ? (
              <button
                onClick={stopExecution}
                style={{
                  width: isMobile ? '42px' : '44px',
                  height: isMobile ? '42px' : '44px',
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: '#ef4444',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isMobile ? '14px' : '16px',
                  flexShrink: 0,
                }}
                title="Stop"
              >
                ■
              </button>
            ) : (
              <button
                onClick={handleSend}
                disabled={!inputValue.trim() || !isConnected || floatingAssistantAccess.disabled}
                style={{
                  width: isMobile ? '42px' : '44px',
                  height: isMobile ? '42px' : '44px',
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: inputValue.trim() && isConnected && !floatingAssistantAccess.disabled ? colors.primary : colors.border,
                  color: '#fff',
                  cursor: inputValue.trim() && isConnected && !floatingAssistantAccess.disabled ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isMobile ? '16px' : '18px',
                  flexShrink: 0,
                }}
                title={floatingAssistantAccess.disabled ? floatingAssistantAccess.blockedMessage : 'Send'}
              >
                ➤
              </button>
            )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Typing animation styles + session action hover */}
      <style>{`
        @keyframes typingDot {
          0%, 60%, 100% { opacity: 0.3; }
          30% { opacity: 1; }
        }
        .typing-dot {
          animation: typingDot 1.4s infinite;
        }
        @keyframes voicePulse {
          0% { box-shadow: 0 0 0 0 rgba(244,67,54,0.5); }
          70% { box-shadow: 0 0 0 10px rgba(244,67,54,0); }
          100% { box-shadow: 0 0 0 0 rgba(244,67,54,0); }
        }
        .session-actions {
          opacity: 0;
          transition: opacity 0.15s;
        }
        div:hover > .session-actions {
          opacity: 1 !important;
        }
      `}</style>
    </div>,
    document.body
  );
};

export default AgentChat;
